/**
 * Thin fetch wrapper around the API.
 *
 * Three jobs: attach the bearer token, unwrap the response envelope, and turn a failure
 * into a typed `ApiError` carrying the server's error code and request id.
 *
 * It also handles the expiry of a 15-minute access token without the user noticing --
 * see `refreshOnce` below.
 */

import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '@/features/auth/session'

/**
 * Empty by default: requests stay same-origin and the Vite dev proxy forwards them to the
 * API, so the browser never needs a CORS preflight.
 *
 * Override with VITE_API_BASE_URL when the app is not served from the same origin as the
 * API -- the Capacitor build in particular, where the page origin is `capacitor://localhost`
 * and a bare `localhost:8010` would resolve to the phone itself, not the dev machine.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const API_PREFIX = '/api/v1'

/**
 * Makes a URL the API handed back usable from this page.
 *
 * Local-storage tickets and file links come back as bare paths (`/api/v1/files/...`),
 * which only work when the page and the API share an origin. In production they do not,
 * so a photo PUT would land on the static web host and be refused. Anything else -- an
 * absolute S3 link, or a path the web app serves itself -- is left alone.
 */
export const apiUrl = <T extends string | null | undefined>(url: T): T =>
  (typeof url === 'string' && url.startsWith(`${API_PREFIX}/`) ? `${BASE_URL}${url}` : url) as T

export interface ApiErrorBody {
  success: false
  error: { code: string; message: string; details?: unknown }
  request_id?: string
}

export interface Envelope<T> {
  success: true
  data: T
  request_id?: string
}

export interface PageMeta {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface Page<T> {
  success: true
  data: T[]
  meta: PageMeta
  request_id?: string
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Raised when the session cannot be recovered and the user has to sign in again. */
export class SessionExpiredError extends ApiError {
  constructor() {
    super(401, 'SESSION_EXPIRED', 'Your session has expired. Please sign in again.')
    this.name = 'SessionExpiredError'
  }
}

interface FetchOptions extends RequestInit {
  /** Skip the bearer header. Used by login and by the refresh call itself. */
  anonymous?: boolean
  /** Internal: prevents a refreshed request from trying to refresh again. */
  _retry?: boolean
}

async function raise(res: Response): Promise<never> {
  let body: Partial<ApiErrorBody> = {}
  try {
    body = await res.json()
  } catch {
    /* non-JSON error response */
  }
  throw new ApiError(
    res.status,
    body.error?.code ?? 'UNKNOWN',
    body.error?.message ?? res.statusText,
    body.error?.details,
    body.request_id,
  )
}

/**
 * Exchange the refresh token for a new pair, at most once at a time.
 *
 * The single-flight promise matters: a dashboard fires several queries at once, and
 * without it every one of them would try to refresh. The server rotates refresh tokens,
 * so the first call would succeed and the rest would present a spent token and be
 * rejected -- signing the user out precisely because the page was busy.
 */
let inFlightRefresh: Promise<void> | null = null

function refreshOnce(): Promise<void> {
  inFlightRefresh ??= (async () => {
    const refresh_token = getRefreshToken()
    if (!refresh_token) throw new SessionExpiredError()

    const res = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token }),
    })

    if (!res.ok) {
      clearTokens()
      throw new SessionExpiredError()
    }

    const body = (await res.json()) as Envelope<{ access_token: string; refresh_token: string }>
    setTokens(body.data)
  })().finally(() => {
    inFlightRefresh = null
  })

  return inFlightRefresh
}

export async function apiFetch<T>(path: string, init: FetchOptions = {}): Promise<T> {
  const { anonymous, _retry, headers, ...rest } = init
  const token = anonymous ? null : getAccessToken()

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
  })

  // An expired access token is the ordinary case, not an error: refresh and replay once.
  // A second 401 means the refresh token is gone too, and the user really is signed out.
  if (res.status === 401 && !anonymous && !_retry) {
    await refreshOnce()
    return apiFetch<T>(path, { ...init, _retry: true })
  }

  if (!res.ok) return raise(res)
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

/** Unwrap `{ success, data }` for the endpoints that return a single object. */
export async function apiGet<T>(path: string, init?: FetchOptions): Promise<T> {
  const body = await apiFetch<Envelope<T>>(path, init)
  return body.data
}

/**
 * Fetch a file the API guards behind a permission check.
 *
 * A plain `<a href>` or `<img src>` cannot carry the bearer token, so anything the server
 * only shows to an authorised user -- an invoice PDF, say -- has to come back through
 * fetch and be handed to the browser as a blob URL. Same refresh-and-replay behaviour as
 * `apiFetch`, because a download is exactly the kind of slow action a token expires during.
 */
export async function apiBlob(path: string, init: FetchOptions = {}): Promise<Blob> {
  const { anonymous, _retry, headers, ...rest } = init
  const token = anonymous ? null : getAccessToken()

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(headers ?? {}),
    },
  })

  if (res.status === 401 && !anonymous && !_retry) {
    await refreshOnce()
    return apiBlob(path, { ...init, _retry: true })
  }

  if (!res.ok) return raise(res)
  return res.blob()
}

export interface HealthResponse {
  status: string
  service: string
  version: string
  environment: string
}

export const getHealth = () => apiFetch<HealthResponse>('/health', { anonymous: true })
